import React, { useState, useEffect } from 'react'

const MobileOptimizer = () => {
  const [connectionSpeed, setConnectionSpeed] = useState('fast')
  const [supportsWebP, setSupportsWebP] = useState(false)

  // Detect connection speed and WebP support
  useEffect(() => {
    // Check connection speed
    if ('connection' in navigator) {
      const conn = navigator.connection
      const slowConnections = ['slow-2g', '2g']
      if (slowConnections.includes(conn.effectiveType) || conn.saveData) {
        setConnectionSpeed('slow')
      } else if (conn.effectiveType === '3g') {
        setConnectionSpeed('medium')
      }
    }

    // Check WebP support
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    const dataURL = canvas.toDataURL('image/webp')
    setSupportsWebP(dataURL.indexOf('data:image/webp') === 0)
  }, [])

  const getOptimizedVideoSrc = (originalSrc) => {
    if (originalSrc.includes('ben-loop')) {
      switch (connectionSpeed) {
        case 'slow':
          return '/mobile-optimized/videos/ben-loop-lite.mp4'
        case 'medium':
          return '/mobile-optimized/videos/ben-loop-mobile.mp4'
        default:
          return '/mobile-optimized/videos/ben-loop-mobile.mp4'
      }
    }
    if (originalSrc.includes('hero-transformation')) {
      return '/mobile-optimized/videos/hero-transformation-mobile.mp4'
    }
    return originalSrc
  }

  const getOptimizedImageSrc = (originalSrc) => {
    if (supportsWebP) {
      const webpSrc = originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp')
      // Check if it's a team image
      if (originalSrc.includes('/team/')) {
        return `/mobile-optimized/team/${originalSrc.split('/').pop().replace(/\.(jpg|jpeg|png)$/i, '.webp')}`
      }
      // Check if it's a general image
      if (originalSrc.includes('/images/')) {
        return `/mobile-optimized/images/${originalSrc.split('/').pop().replace(/\.(jpg|jpeg|png)$/i, '.webp')}`
      }
      // For other images, check if optimized version exists
      return `/mobile-optimized/images/${originalSrc.split('/').pop().replace(/\.(jpg|jpeg|png)$/i, '.webp')}`
    }
    return originalSrc
  }

  // Create global optimization functions
  window.getOptimizedVideoSrc = getOptimizedVideoSrc
  window.getOptimizedImageSrc = getOptimizedImageSrc
  window.connectionSpeed = connectionSpeed
  window.supportsWebP = supportsWebP

  return null // This is a utility component, no rendering
}

// Lazy Image Component
export const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  style = {}, 
  onLoad = () => {}, 
  ...props 
}) => {
  const [loaded, setLoaded] = useState(false)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const img = document.createElement('img')
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (img) {
      observer.observe(img)
    }

    return () => observer.disconnect()
  }, [])

  const handleLoad = () => {
    setLoaded(true)
    onLoad()
  }

  const optimizedSrc = window.getOptimizedImageSrc ? window.getOptimizedImageSrc(src) : src

  return (
    <div className={`lazy-image-container ${className}`} style={style}>
      {inView && (
        <img
          src={optimizedSrc}
          alt={alt}
          onLoad={handleLoad}
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            ...style
          }}
          {...props}
        />
      )}
      {!loaded && inView && (
        <div 
          className="lazy-placeholder"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '14px'
          }}
        >
          Chargement...
        </div>
      )}
    </div>
  )
}

// Lazy Video Component
export const LazyVideo = ({ 
  src, 
  poster, 
  className = '', 
  style = {}, 
  autoPlay = false,
  loop = false,
  muted = true,
  playsInline = true,
  ...props 
}) => {
  const [loaded, setLoaded] = useState(false)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const videoElement = document.createElement('video')
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (videoElement) {
      observer.observe(videoElement)
    }

    return () => observer.disconnect()
  }, [])

  const handleLoad = () => {
    setLoaded(true)
  }

  const optimizedSrc = window.getOptimizedVideoSrc ? window.getOptimizedVideoSrc(src) : src
  const optimizedPoster = window.getOptimizedImageSrc && poster ? window.getOptimizedImageSrc(poster) : poster

  return (
    <div className={`lazy-video-container ${className}`} style={style}>
      {inView && (
        <video
          src={optimizedSrc}
          poster={optimizedPoster}
          onLoadedData={handleLoad}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            ...style
          }}
          {...props}
        />
      )}
      {!loaded && inView && poster && (
        <img
          src={optimizedPoster}
          alt="Vidéo en cours de chargement"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...style
          }}
        />
      )}
    </div>
  )
}

export default MobileOptimizer